import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiCheck, FiArrowRight, FiStar, FiDollarSign, FiTrendingUp, FiZap, FiAward, FiPlusCircle } = FiIcons;

const PricingPlans = () => {
  return (
    <section id="pricing" className="py-24 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Choose Your MVP Package
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Select the plan that fits your startup stage and requirements.
            All packages include our 72-hour delivery guarantee.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-8 mb-16">
          {/* Tier 1: Launch */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true }}
            className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100"
          >
            <div className="p-8">
              <div className="flex items-center mb-4">
                <SafeIcon icon={FiPlusCircle} className="w-7 h-7 text-blue-500 mr-3" />
                <h3 className="text-2xl font-bold text-gray-900">Launch</h3>
              </div>
              
              <div className="flex items-baseline mb-6">
                <span className="text-4xl font-extrabold text-gray-900">$2,500</span>
                <span className="text-gray-600 ml-2">USD</span>
              </div>
              
              <p className="text-gray-600 mb-6">
                For early-stage founders validating an idea.
              </p>
              
              <ul className="space-y-4 mb-8">
                <li className="flex items-start">
                  <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-500 mr-3 mt-0.5" />
                  <span>MVP delivered in 72 hours</span>
                </li>
                <li className="flex items-start">
                  <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-500 mr-3 mt-0.5" />
                  <span>Google Auth, Stripe, Supabase</span>
                </li>
                <li className="flex items-start">
                  <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-500 mr-3 mt-0.5" />
                  <span>One API integration</span>
                </li>
                <li className="flex items-start">
                  <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-500 mr-3 mt-0.5" />
                  <span>One feedback-based iteration</span>
                </li>
                <li className="flex items-start">
                  <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-500 mr-3 mt-0.5" />
                  <span>Simple UI/UX design</span>
                </li>
              </ul>
            </div>
            
            <div className="px-8 pb-8">
              <button className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-lg py-4 px-6 font-semibold transition-colors duration-200 flex items-center justify-center">
                Start with Launch
                <SafeIcon icon={FiArrowRight} className="ml-2 w-5 h-5" />
              </button>
            </div>
          </motion.div>

          {/* Tier 2: Growth (Recommended) */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="bg-white rounded-2xl shadow-xl overflow-hidden border-2 border-blue-500 transform lg:scale-105 z-10"
          >
            <div className="bg-blue-500 py-2 px-8">
              <div className="flex items-center justify-center">
                <SafeIcon icon={FiStar} className="w-5 h-5 text-yellow-300 mr-2" />
                <span className="text-white font-medium">RECOMMENDED</span>
              </div>
            </div>
            
            <div className="p-8">
              <div className="flex items-center mb-4">
                <SafeIcon icon={FiZap} className="w-7 h-7 text-blue-500 mr-3" />
                <h3 className="text-2xl font-bold text-gray-900">Growth</h3>
              </div>
              
              <div className="flex items-baseline mb-6">
                <span className="text-4xl font-extrabold text-gray-900">$5,000</span>
                <span className="text-gray-600 ml-2">USD</span>
              </div>
              
              <p className="text-gray-600 mb-6">
                For startups pitching to investors or launching publicly.
              </p>
              
              <ul className="space-y-4 mb-8">
                <li className="flex items-start">
                  <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-500 mr-3 mt-0.5" />
                  <span>Everything in Launch</span>
                </li>
                <li className="flex items-start">
                  <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-500 mr-3 mt-0.5" />
                  <span>2 additional integrations (e.g., chatbot, maps)</span>
                </li>
                <li className="flex items-start">
                  <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-500 mr-3 mt-0.5" />
                  <span>Custom landing page</span>
                </li>
                <li className="flex items-start">
                  <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-500 mr-3 mt-0.5" />
                  <span>Email onboarding flow</span>
                </li>
                <li className="flex items-start">
                  <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-500 mr-3 mt-0.5" />
                  <span>Two iteration cycles</span>
                </li>
                <li className="flex items-start">
                  <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-500 mr-3 mt-0.5" />
                  <span>2 weeks of post-launch support</span>
                </li>
              </ul>
            </div>
            
            <div className="px-8 pb-8">
              <button className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-lg py-4 px-6 font-semibold transition-all duration-200 shadow-md flex items-center justify-center">
                Choose Growth Plan
                <SafeIcon icon={FiArrowRight} className="ml-2 w-5 h-5" />
              </button>
            </div>
          </motion.div>

          {/* Tier 3: Scale */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            viewport={{ once: true }}
            className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100"
          >
            <div className="p-8">
              <div className="flex items-center mb-4">
                <SafeIcon icon={FiTrendingUp} className="w-7 h-7 text-blue-500 mr-3" />
                <h3 className="text-2xl font-bold text-gray-900">Scale</h3>
              </div>
              
              <div className="flex items-baseline mb-6">
                <span className="text-4xl font-extrabold text-gray-900">$10,000</span>
                <span className="text-gray-600 ml-2">USD</span>
              </div>
              
              <p className="text-gray-600 mb-6">
                For founders building a premium MVP for scale.
              </p>
              
              <ul className="space-y-4 mb-8">
                <li className="flex items-start">
                  <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-500 mr-3 mt-0.5" />
                  <span>Everything in Growth</span>
                </li>
                <li className="flex items-start">
                  <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-500 mr-3 mt-0.5" />
                  <span>Fully custom UI/UX with transitions</span>
                </li>
                <li className="flex items-start">
                  <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-500 mr-3 mt-0.5" />
                  <span>Up to 5 custom integrations</span>
                </li>
                <li className="flex items-start">
                  <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-500 mr-3 mt-0.5" />
                  <span>AI assistant (optional)</span>
                </li>
                <li className="flex items-start">
                  <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-500 mr-3 mt-0.5" />
                  <span>1 month of technical support</span>
                </li>
                <li className="flex items-start">
                  <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-500 mr-3 mt-0.5" />
                  <span>4 iteration cycles</span>
                </li>
                <li className="flex items-start">
                  <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-500 mr-3 mt-0.5" />
                  <span>Optional pitch deck + technical documentation</span>
                </li>
              </ul>
            </div>
            
            <div className="px-8 pb-8">
              <button className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-lg py-4 px-6 font-semibold transition-colors duration-200 flex items-center justify-center">
                Go Premium
                <SafeIcon icon={FiArrowRight} className="ml-2 w-5 h-5" />
              </button>
            </div>
          </motion.div>
        </div>

        {/* Footer Note */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          viewport={{ once: true }}
          className="max-w-4xl mx-auto text-center bg-blue-50 rounded-xl p-6 border border-blue-100"
        >
          <div className="flex items-center justify-center mb-4">
            <SafeIcon icon={FiAward} className="w-6 h-6 text-blue-600 mr-2" />
            <h4 className="text-xl font-bold text-gray-900">Industry-Leading Value</h4>
          </div>
          <p className="text-gray-700">
            Typical MVP development costs range from $15,000 to $150,000. With MVP72, you get a launch-ready product in just 3 days — for a fraction of the price.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default PricingPlans;